import Vue from 'vue'
import { isSamePath as _isSamePath, joinURL, normalizeURL, withQuery, withoutTrailingSlash } from 'ufo'

// window.{{globals.loadedCallback}} hook
// Useful for jsdom testing or plugins (https://github.com/tmpvar/jsdom#dealing-with-asynchronous-script-loading)
if (process.client) {
  window.onNuxtReadyCbs = []
  window.onNuxtReady = (cb) => {
    window.onNuxtReadyCbs.push(cb)
  }
}

export function createGetCounter (counterObject, defaultKey = '') {
  return function getCounter (id = defaultKey) {
    if (counterObject[id] === undefined) {
      counterObject[id] = 0
    }
    return counterObject[id]++
  }
}

export function empty () {}

export function globalHandleError (error) {
  if (Vue.config.errorHandler) {
    Vue.config.errorHandler(error)
  }
}

export function interopDefault (promise) {
  return promise.then(m => m.default || m)
}

export function hasFetch(vm) {
  return vm.$options && typeof vm.$options.fetch === 'function' && !vm.$options.fetch.length
}
export function purifyData(data) {
  if (process.env.NODE_ENV === 'production') {
    return data
  }

  return Object.entries(data).filter(
    ([key, value]) => {
      const valid = !(value instanceof Function) && !(value instanceof Promise)
      if (!valid) {
        console.warn(`${key} is not able to be stringified. This will break in a production environment.`)
      }
      return valid
    }
    ).reduce((obj, [key, value]) => {
      obj[key] = value
      return obj
    }, {})
}
export function getChildrenComponentInstancesUsingFetch(vm, instances = []) {
  const children = vm.$children || []
  for (const child of children) {
    if (child.$fetch) {
      instances.push(child)
      continue; // Don't get the children since it will reload the template
    }
    if (child.$children) {
      getChildrenComponentInstancesUsingFetch(child, instances)
    }
  }
  return instances
}

export function applyAsyncData (Component, asyncData) {
  if (
    // For SSR, we once all this function without second param to just apply asyncData
    // Prevent doing this for each SSR request
    !asyncData && Component.options.__hasNuxtData
  ) {
    return
  }

  const ComponentData = Component.options._originDataFn || Component.options.data || function () { return {} }
  Component.options._originDataFn = ComponentData

  Component.options.data = function () {
    const data = ComponentData.call(this, this)
    if (this.$ssrContext) {
      asyncData = this.$ssrContext.asyncData[Component.cid]
    }
    return { ...data, ...asyncData }
  }

  Component.options.__hasNuxtData = true

  if (Component._Ctor && Component._Ctor.options) {
    Component._Ctor.options.data = Component.options.data
  }
}

export function sanitizeComponent (Component) {
  // If Component already sanitized
  if (Component.options && Component._Ctor === Component) {
    return Component
  }
  if (!Component.options) {
    Component = Vue.extend(Component) // fix issue #6
    Component._Ctor = Component
  } else {
    Component._Ctor = Component
    Component.extendOptions = Component.options
  }
  // If no component name defined, set file path as name, (also fixes #5703)
  if (!Component.options.name && Component.options.__file) {
    Component.options.name = Component.options.__file
  }
  return Component
}

export function getMatchedComponents (route, matches = false, prop = 'components') {
  return Array.prototype.concat.apply([], route.matched.map((m, index) => {
    return Object.keys(m[prop]).map((key) => {
      matches && matches.push(index)
      return m[prop][key]
    })
  }))
}

export function getMatchedComponentsInstances (route, matches = false) {
  return getMatchedComponents(route, matches, 'instances')
}

export function flatMapComponents (route, fn) {
  return Array.prototype.concat.apply([], route.matched.map((m, index) => {
    return Object.keys(m.components).reduce((promises, key) => {
      if (m.components[key]) {
        promises.push(fn(m.components[key], m.instances[key], m, key, index))
      } else {
        delete m.components[key]
      }
      return promises
    }, [])
  }))
}

export function resolveRouteComponents (route, fn) {
  return Promise.all(
    flatMapComponents(route, async (Component, instance, match, key) => {
      // If component is a function, resolve it
      if (typeof Component === 'function' && !Component.options) {
        try {
          Component = await Component()
        } catch (error) {
          // Handle webpack chunk loading errors
          // This may be due to a new deployment or a network problem
          if (
            error &&
            error.name === 'ChunkLoadError' &&
            typeof window !== 'undefined' &&
            window.sessionStorage
          ) {
            const timeNow = Date.now()
            const previousReloadTime = parseInt(window.sessionStorage.getItem('nuxt-reload'))

            // check for previous reload time not to reload infinitely
            if (!previousReloadTime || previousReloadTime + 60000 < timeNow) {
              window.sessionStorage.setItem('nuxt-reload', timeNow)
              window.location.reload(true /* skip cache */)
            }
          }

          throw error
        }
      }
      match.components[key] = Component = sanitizeComponent(Component)
      return typeof fn === 'function' ? fn(Component, instance, match, key) : Component
    })
  )
}

export async function getRouteData (route) {
  if (!route) {
    return
  }
  // Make sure the components are resolved (code-splitting)
  await resolveRouteComponents(route)
  // Send back a copy of route with meta based on Component definition
  return {
    ...route,
    meta: getMatchedComponents(route).map((Component, index) => {
      return { ...Component.options.meta, ...(route.matched[index] || {}).meta }
    })
  }
}

export async function setContext (app, context) {
  // If context not defined, create it
  if (!app.context) {
    app.context = {
      isStatic: process.static,
      isDev: false,
      isHMR: false,
      app,
      store: app.store,
      payload: context.payload,
      error: context.error,
      base: app.router.options.base,
      env: {"PREFIX":"fl88","MOBILE_INDEX_TYPE":1,"SHORT_NANE":"發樂","WEB_TITLE":"發樂娛樂城","WEB_NAME":"發樂娛樂城","PageHead":{"layouts":{"domain":"https://fale88.com","noMobile":true,"link":[{"hid":"canonical","rel":"canonical","href":""}],"meta":[{"hid":"robots","name":"robots","content":"index, follow"}],"script":[{"type":"application/ld+json","json":{"@context":"https://schema.org","@type":"EntertainmentBusiness","name":"發樂娛樂城","description":"發樂娛樂城是台灣娛樂城的領導品牌，出金速度快又穩，不定期推出超殺返水優惠，遊戲包含真人百家樂、體育彩票、老虎機、樂透彩球、捕魚棋牌等，快註冊下載等您來玩！","image":"https://fale88.com/icons/logo.png","logo":"https://fale88.com/icons/logo.png","@id":"https://fale88.com/#localbusiness","url":"https://fale88.com","brand":{"@type":"Organization","@id":"https://fale88.com/#organization","name":"發樂娛樂城","alternateName":"發樂","url":"https://fale88.com","logo":"https://fale88.com/icons/logo.png","image":"https://fale88.com/icons/logo.png"},"areaServed":"TW","legalName":"發樂娛樂城","address":{"@type":"PostalAddress","streetAddress":"內湖區成功路三段8號88樓","addressLocality":"台北市","postalCode":"114","addressCountry":"TW"},"location":{"@type":"PostalAddress","streetAddress":"內湖區成功路三段8號88樓","addressLocality":"台北市","postalCode":"114","addressCountry":"TW"},"openingHoursSpecification":{"@type":"OpeningHoursSpecification","dayOfWeek":["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"],"opens":"00:00","closes":"23:59"}}},{"type":"application/ld+json","json":{"@context":"https://schema.org","@type":"Organization","name":"發樂娛樂城","alternateName":"發樂","url":"https://fale88.com","logo":"https://fale88.com/icons/logo.png","description":"發樂娛樂城是台灣娛樂城的領導品牌，出金速度快又穩，不定期推出超殺返水優惠，遊戲包含真人百家樂、體育彩票、老虎機、樂透彩球、捕魚棋牌等，快註冊下載等您來玩！","sameAs":["https://www.facebook.com/fale88com","https://blog.fale88.com","https://www.instagram.com/fale88com/"]}},{"type":"application/ld+json","json":{"@context":"http://schema.org","@type":"WebSite","name":"發樂娛樂城 - 台灣娛樂城的領導品牌","description":"發樂娛樂城是台灣娛樂城的領導品牌，出金速度快又穩，不定期推出超殺返水優惠，遊戲包含真人百家樂、體育彩票、老虎機、樂透彩球、捕魚棋牌等，快註冊下載等您來玩！","url":"","author":{"@context":"https://schema.org","@type":"Organization","name":"發樂娛樂城"},"creator":{"@context":"https://schema.org","@type":"Organization","name":"發樂娛樂城"},"keywords":"娛樂城,發樂,線上娛樂城"}},{"type":"application/ld+json","json":{"@context":"http://schema.org","@type":"ItemList","itemListElement":[{"@type":"SiteNavigationElement","position":1,"name":"優惠活動","description":"瀏覽發樂娛樂城的最新優惠活動資訊。","url":"/activity/activity"},{"@type":"SiteNavigationElement","position":2,"name":"體育賽事","description":"最頂級的super運彩投注系統，國內外的體育賽事：足球、籃球、棒球、美式足球等應有盡有，並為不同賽事推出玩法，如足球投注的獨贏、場中投注等，賠率更勝國內運彩賠率，並內建賽事直播和即時比分！","url":"/game/sport"},{"@type":"SiteNavigationElement","position":3,"name":"真人娛樂","description":"廣受玩家好評的四大真人視訊平台：歐博、SA、WM、DREAM GAMING，皆為國內外知名的線上百家樂大品牌，讓喜愛百家樂的玩家們可依照喜好及習慣來挑選最適合自己的投注平台。","url":"/game/live"},{"@type":"SiteNavigationElement","position":4,"name":"電子遊戲","description":"愛好老虎機的台灣玩家們一致認為爆分率最高的三個品牌：BWIN電子、KA電子、GR電子。其中BWIN及KA電子更提供免費老虎機試玩，讓玩家隨時隨地皆可輕鬆無壓力的暢遊老虎機遊戲世界。","url":"/game/egame"},{"@type":"SiteNavigationElement","position":5,"name":"樂透彩球","description":"提供備受台灣彩玩家們熱愛的三大遊戲平台：101彩票、BingoEX、SG88，其中囊括大樂透、六合彩、今彩539、時時彩、快樂8、北京賽車以及Bingo等熱門彩球遊戲！","url":"/game/lottery"},{"@type":"SiteNavigationElement","position":6,"name":"棋牌遊戲","description":"知名品牌：好路棋牌，從大老二、牛牛、德州撲克到推筒子、炸金花等各大火爆熱門的棋牌類遊戲應有盡有，搭配一流的音效及高品質遊戲畫面，呈現給玩家最舒適的棋牌享受。","url":"/game/chess"},{"@type":"SiteNavigationElement","position":7,"name":"捕魚遊戲","description":"BWIN電子、KA電子、GR電子等三大平台總共多達15款市面上熱門的捕魚機遊戲，並附有免費試玩，讓玩家們能夠徜徉在不同情境的捕魚機中盡情遊玩，細細品味每款不同主題的捕魚遊戲。","url":"/game/fish"},{"@type":"SiteNavigationElement","position":8,"name":"部落格","description":"瀏覽發樂娛樂城的部落格。","url":" https://blog.fale88.com"}]}}]},"index":{"script":[{"type":"application/ld+json","json":{"@context":"https://schema.org","@type":"BreadcrumbList","itemListElement":[{"@type":"ListItem","position":1,"name":"發樂娛樂城","item":""}]}}],"desktop":{"link":[{"rel":"alternate","media":"only screen and (max-width: 640px)","href":"/mobile"}]}},"code":{"link":[{"hid":"canonical","rel":"canonical","href":"https://fale88.com"}]},"signup":{"title":"會員註冊 - 發樂娛樂城","description":"","meta":[{"hid":"apple-mobile-web-app-title","name":"apple-mobile-web-app-title","content":"title"},{"hid":"og:title","name":"og:title","content":"title"},{"hid":"description","name":"","content":""},{"hid":"og:description","name":"","content":""}],"desktop":{"link":[{"rel":"alternate","media":"only screen and (max-width: 640px)","href":"/mobile/account/signup"}]}},"login":{"title":"會員登入 - 發樂娛樂城","description":"","meta":[{"hid":"apple-mobile-web-app-title","name":"apple-mobile-web-app-title","content":"title"},{"hid":"og:title","name":"og:title","content":"title"},{"hid":"description","name":"","content":""},{"hid":"og:description","name":"","content":""}],"desktop":{"link":[{"rel":"alternate","media":"only screen and (max-width: 640px)","href":"/mobile/account/login"}]}},"about":{"title":"關於我們 - 發樂娛樂城","meta":[{"hid":"apple-mobile-web-app-title","name":"apple-mobile-web-app-title","content":"title"},{"hid":"og:title","name":"og:title","content":"title"},{"hid":"description","name":"","content":""},{"hid":"og:description","name":"","content":""}],"script":[{"type":"application/ld+json","json":{"@context":"https://schema.org","@type":"BreadcrumbList","itemListElement":[{"@type":"ListItem","position":1,"name":"發樂娛樂城","item":""},{"@type":"ListItem","position":2,"name":"關於我們","item":"/help/about"}]}}],"desktop":{"link":[{"rel":"alternate","media":"only screen and (max-width: 640px)","href":"/mobile/help/about"}]}},"question":{"title":"常見問題 - 發樂娛樂城","meta":[{"hid":"apple-mobile-web-app-title","name":"apple-mobile-web-app-title","content":"title"},{"hid":"og:title","name":"og:title","content":"title"},{"hid":"description","name":"","content":""},{"hid":"og:description","name":"","content":""}],"script":[{"type":"application/ld+json","json":{"@context":"https://schema.org","@type":"BreadcrumbList","itemListElement":[{"@type":"ListItem","position":1,"name":"發樂娛樂城","item":""},{"@type":"ListItem","position":2,"name":"常見問題","item":"/help/question"}]}}],"desktop":{"link":[{"rel":"alternate","media":"only screen and (max-width: 640px)","href":"/mobile/help/question"}]}},"disclaimer":{"title":"隱私權&安全性 - 發樂娛樂城","meta":[{"hid":"apple-mobile-web-app-title","name":"apple-mobile-web-app-title","content":"title"},{"hid":"og:title","name":"og:title","content":"title"},{"hid":"description","name":"","content":""},{"hid":"og:description","name":"","content":""}],"script":[{"type":"application/ld+json","json":{"@context":"https://schema.org","@type":"BreadcrumbList","itemListElement":[{"@type":"ListItem","position":1,"name":"發樂娛樂城","item":""},{"@type":"ListItem","position":2,"name":"隱私權&安全性","item":"/help/disclaimer"}]}}],"desktop":{"link":[{"rel":"alternate","media":"only screen and (max-width: 640px)","href":"/mobile/help/disclaimer"}]}},"rule":{"title":"條款與條件 - 發樂娛樂城","meta":[{"hid":"apple-mobile-web-app-title","name":"apple-mobile-web-app-title","content":"title"},{"hid":"og:title","name":"og:title","content":"title"},{"hid":"description","name":"","content":""},{"hid":"og:description","name":"","content":""}],"script":[{"type":"application/ld+json","json":{"@context":"https://schema.org","@type":"BreadcrumbList","itemListElement":[{"@type":"ListItem","position":1,"name":"發樂娛樂城","item":""},{"@type":"ListItem","position":2,"name":"條款與條件","item":"/help/rule"}]}}],"desktop":{"link":[{"rel":"alternate","media":"only screen and (max-width: 640px)","href":"/mobile/help/rule"}]}},"check":{"title":"清緩存教學 - 發樂娛樂城","meta":[{"hid":"apple-mobile-web-app-title","name":"apple-mobile-web-app-title","content":"title"},{"hid":"og:title","name":"og:title","content":"title"},{"hid":"description","name":"","content":""},{"hid":"og:description","name":"","content":""}],"script":[{"type":"application/ld+json","json":{"@context":"https://schema.org","@type":"BreadcrumbList","itemListElement":[{"@type":"ListItem","position":1,"name":"發樂娛樂城","item":""},{"@type":"ListItem","position":2,"name":"清除快取","item":"/help/check"}]}}],"desktop":{"link":[{"rel":"alternate","media":"only screen and (max-width: 640px)","href":"/mobile/help/check"}]}},"activity":{"title":"優惠活動 - 發樂娛樂城","description":"最新的發樂娛樂城優惠活動資訊，提供首存贈送、全民代理及領先業界的高額返水，且不定時舉辦各種娛樂城限時特惠，歡迎玩家們熱情關注，踴躍參加。","meta":[{"hid":"apple-mobile-web-app-title","name":"apple-mobile-web-app-title","content":"title"},{"hid":"og:title","name":"og:title","content":"title"},{"hid":"description","name":"description","content":"description"},{"hid":"og:description","name":"og:description","content":"description"}],"script":[{"type":"application/ld+json","json":{"@context":"https://schema.org","@type":"BreadcrumbList","itemListElement":[{"@type":"ListItem","position":1,"name":"發樂娛樂城","item":""},{"@type":"ListItem","position":2,"name":"優惠活動","item":"/activity/activity"}]}}],"desktop":{"link":[{"rel":"alternate","media":"only screen and (max-width: 640px)","href":"/mobile/activity/activity"}]}},"sport":{"title":"體育賽事 - 發樂娛樂城","description":"發樂娛樂城提供最頂級的super運彩投注系統，國內外的體育賽事：足球、籃球、棒球、美式足球等應有盡有，並為不同賽事推出玩法，如足球投注的獨贏、場中投注等，賠率更勝國內運彩賠率，並內建賽事直播和即時比分！","meta":[{"hid":"apple-mobile-web-app-title","name":"apple-mobile-web-app-title","content":"title"},{"hid":"og:title","name":"og:title","content":"title"},{"hid":"description","name":"description","content":"description"},{"hid":"og:description","name":"og:description","content":"description"}],"script":[{"type":"application/ld+json","json":{"@context":"https://schema.org","@type":"BreadcrumbList","itemListElement":[{"@type":"ListItem","position":1,"name":"發樂娛樂城","item":""},{"@type":"ListItem","position":2,"name":"體育賽事","item":"/game/sport"}]}}],"desktop":{"link":[{"rel":"alternate","media":"only screen and (max-width: 640px)","href":"/mobile/game/sport"}]}},"live":{"title":"真人娛樂 - 發樂娛樂城","description":"發樂娛樂城提供廣受玩家好評的四大真人視訊平台：歐博、SA、WM、DREAM GAMING，皆為國內外知名的線上百家樂大品牌，讓喜愛百家樂的玩家們可依照喜好及習慣來挑選最適合自己的投注平台。","meta":[{"hid":"apple-mobile-web-app-title","name":"apple-mobile-web-app-title","content":"title"},{"hid":"og:title","name":"og:title","content":"title"},{"hid":"description","name":"description","content":"description"},{"hid":"og:description","name":"og:description","content":"description"}],"script":[{"type":"application/ld+json","json":{"@context":"https://schema.org","@type":"BreadcrumbList","itemListElement":[{"@type":"ListItem","position":1,"name":"發樂娛樂城","item":""},{"@type":"ListItem","position":2,"name":"真人娛樂","item":"/game/live"}]}}],"desktop":{"link":[{"rel":"alternate","media":"only screen and (max-width: 640px)","href":"/mobile/game/live"}]}},"egame":{"title":"電子遊戲 - 發樂娛樂城","description":"發樂娛樂城提供愛好老虎機的玩家們一致認為爆分率最高的三個品牌：BWIN電子、KA電子、GR電子。其中BWIN及KA電子更提供免費老虎機試玩，讓玩家隨時隨地皆可輕鬆無壓力的暢遊老虎機遊戲世界。","meta":[{"hid":"apple-mobile-web-app-title","name":"apple-mobile-web-app-title","content":"title"},{"hid":"og:title","name":"og:title","content":"title"},{"hid":"description","name":"description","content":"description"},{"hid":"og:description","name":"og:description","content":"description"}],"script":[{"type":"application/ld+json","json":{"@context":"https://schema.org","@type":"BreadcrumbList","itemListElement":[{"@type":"ListItem","position":1,"name":"發樂娛樂城","item":""},{"@type":"ListItem","position":2,"name":"電子遊戲","item":"/game/egame"}]}}],"desktop":{"link":[{"rel":"alternate","media":"only screen and (max-width: 640px)","href":"/mobile/game/egame"}]}},"lottery":{"title":"樂透彩球 - 發樂娛樂城","description":"發樂娛樂城提供備受台灣彩玩家們熱愛的三大遊戲平台：101彩票、BingoEX、SG88，其中囊括大樂透、六合彩、今彩539、時時彩、快樂8、北京賽車以及Bingo等熱門彩球遊戲！","meta":[{"hid":"apple-mobile-web-app-title","name":"apple-mobile-web-app-title","content":"title"},{"hid":"og:title","name":"og:title","content":"title"},{"hid":"description","name":"description","content":"description"},{"hid":"og:description","name":"og:description","content":"description"}],"script":[{"type":"application/ld+json","json":{"@context":"https://schema.org","@type":"BreadcrumbList","itemListElement":[{"@type":"ListItem","position":1,"name":"發樂娛樂城","item":""},{"@type":"ListItem","position":2,"name":"樂透彩球","item":"/game/lottery"}]}}],"desktop":{"link":[{"rel":"alternate","media":"only screen and (max-width: 640px)","href":"/mobile/game/lottery"}]}},"chess":{"title":"棋牌遊戲 - 發樂娛樂城","description":"發樂娛樂城深知玩家們對棋牌遊戲的愛好，提供知名品牌：好路棋牌供玩家暢遊，從大老二、牛牛、德州撲克到推筒子、炸金花等各大火爆熱門的棋牌類遊戲應有盡有，搭配一流的音效及高品質遊戲畫面，呈現給玩家最舒適的棋牌享受。","meta":[{"hid":"apple-mobile-web-app-title","name":"apple-mobile-web-app-title","content":"title"},{"hid":"og:title","name":"og:title","content":"title"},{"hid":"description","name":"description","content":"description"},{"hid":"og:description","name":"og:description","content":"description"}],"script":[{"type":"application/ld+json","json":{"@context":"https://schema.org","@type":"BreadcrumbList","itemListElement":[{"@type":"ListItem","position":1,"name":"發樂娛樂城","item":""},{"@type":"ListItem","position":2,"name":"棋牌遊戲","item":"/game/chess"}]}}],"desktop":{"link":[{"rel":"alternate","media":"only screen and (max-width: 640px)","href":"/mobile/game/chess"}]}},"fish":{"title":"捕魚遊戲 - 發樂娛樂城","description":"發樂娛樂城提供BWIN電子、KA電子、GR電子等三大平台總共多達15款市面上熱門的捕魚機遊戲，並附有免費試玩，讓玩家們能夠徜徉在不同情境的捕魚機中盡情遊玩，細細品味每款不同主題的捕魚遊戲。","meta":[{"hid":"apple-mobile-web-app-title","name":"apple-mobile-web-app-title","content":"title"},{"hid":"og:title","name":"og:title","content":"title"},{"hid":"description","name":"description","content":"description"},{"hid":"og:description","name":"og:description","content":"description"}],"script":[{"type":"application/ld+json","json":{"@context":"https://schema.org","@type":"BreadcrumbList","itemListElement":[{"@type":"ListItem","position":1,"name":"發樂娛樂城","item":""},{"@type":"ListItem","position":2,"name":"捕魚遊戲","item":"/game/fish"}]}}],"desktop":{"link":[{"rel":"alternate","media":"only screen and (max-width: 640px)","href":"/mobile/game/fish"}]}}},"LoadingColor":"#0f5f70","DEF_webStyle":"fl88","DEF_webFrame":"frame03s","DEF_dialogTipEntity":"1","DEF_dialogTipDigit":"1","DEF_language":"default","DEF_daynight":"default","DEF_audioplay":"common/audioPlay","pc_marquee":"idxMarquee","pc_marquee04":"default","pc_header":"header/Header11","pc_game":"idxGame06","pc_info":"idxInfo08","pc_idxDownload":"idxDownload","pc_jackpot":"idxComJackpot","pc_footer":"idxFooter01","pc_falling":"default","pc_header_li":"1","pc_member_show_appendLink":"0","pc_member_link_appendLink":null,"M_DEF_webFrame":"m_frame08","M_header_ham":"0","M_header_user":"1","M_header_btn":"1","M_header_sub":"1","M_dialogAPP":"0","M_header":"default","M_main01":"idxSlider2","M_main02":"idxMarquee","M_main03":"header/m_tabBtn01","M_main04":"idxHotpro11r","M_main05":"default","M_main06":"default","M_Footer":"default","M_Fmenu":"default","M_game_page":"0","M_GameBtn":"1","M_ExLink":"0","M_HideMenu":"header/m_ham08","M_dialogCSR":"dialogCSR_circle","M_Login":"login","M_Signup":"signup","APP_NAME":"Laravel","APP_ENV":"production","APP_KEY":"base64:IJhbAXbEAvg+wDewUoszWFdDtq97Y4DYOXLvI5wZg0I=","APP_DEBUG":"false","APP_URL":"http://localhost","APP_LOCALE":"zh-TW","APP_TimeZone":"Asia/Taipei","APP_API_DOMAIN":"fl88.local.api","APP_BACKEND_DOMAIN":"a6yjpy.fale88.com","APP_FRONTEND_DOMAIN":"fale77.com","APP_CALLBACK_DOMAIN":"api.fale77.com","AGENT_API_DOMAIN":"agent.fale88.com","APP_IMAGES_DOMAIN":"//img.fale88.com","LOG_CHANNEL":"daily","DB_CONNECTION":"int","DB_HOST":"172.31.101.113","DB_PORT":"3306","DB_DATABASE":"FL88_Integration","DB_DATABASE_AG":"FL88_Agency","DB_DATABASE_MA":"FL88_Management","DB_DATABASE_MS":"FL88_MoneySystem","DB_USERNAME":"fl88_admin","DB_PASSWORD":"$QtHEFCXuP4zXk?Y","BROADCAST_DRIVER":"log","CACHE_DRIVER":"file","QUEUE_CONNECTION":"sync","SESSION_DRIVER":"redis","SESSION_CONNECTION":"session","REDIS_HOST":"127.0.0.1","REDIS_PASSWORD":"null","REDIS_PORT":"6379","MAIL_DRIVER":"smtp","MAIL_HOST":"smtp.mailtrap.io","MAIL_PORT":"2525","MAIL_USERNAME":"null","MAIL_PASSWORD":"null","MAIL_ENCRYPTION":"null","MAIL_FROM_ADDRESS":"null","MAIL_FROM_NAME":"${APP_NAME}","AWS_ACCESS_KEY_ID":"AKIAZ762NQMON4IOSTE7","AWS_SECRET_ACCESS_KEY":"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","AWS_DEFAULT_REGION":"ap-northeast-1","AWS_BUCKET":"fl88-beyondgaming","AWS_BUCKET_PUBLIC":"bg-public-image","PUSHER_APP_ID":"","PUSHER_APP_KEY":"","PUSHER_APP_SECRET":"","PUSHER_APP_CLUSTER":"mt1","MIX_PUSHER_APP_KEY":"${PUSHER_APP_KEY}","MIX_PUSHER_APP_CLUSTER":"${PUSHER_APP_CLUSTER}","JWT_SECRET":"6fWoBp9k1iVjmAVfmdtzGekkBWDq2bBserpiQqiYf1U5EULthBjJex2BRxni3CB0","CODE_WAIT_TIME":"300","IMG_DISK":"s3","IMG_DISK_PUBLIC":"s3Public","MASTER_ACCOUNT":"account","UPAGENT":"fl88agent","SEND_TELEGRAM_MSG":"1","CALLBACK_BY_API":"0","ALL_CHECK":"1","DEPOSIT_MULTIPLE":"1","TRANSFER_MIN":"1","ACCOUNT_LEN":"9","BACKEND_SELECT_LIMIT":"36500","DATE_LIMIT_RANGE":"10","SHOW_AUTO_POINT":"1","SHOW_DEPOSIT_DISCOUNT":"1","SHOW_SHARE":"1","SHOW_LEVEL":"1","SHOW_CONVERSION":"0","SHOW_DEPOSIT":"1","SHOW_VIRTUAL_CURRENCY":"1","SHOW_ACCOUNT_CARD":"1","SHOW_DISCOUNT":"1","SHOW_JKOPAY_SETTING":"0","SHOW_LINEPAY_SETTING":"0","SIGN_CAPTCHAS":"0","SIGN_MOBILE_CAPTCHAS":"1","TRANSFER_CHAT_ID":"-4772084199","TRANSFER_NOTICE":"1","TRANSFER_AMOUNT":"1000000","LINE_LINK":"https://line.me/","ENABLE_ROULETTE":"1","ENABLE_CHECKIN":"1","DG_LIVE_HANDICAP_DEFAULT_MINBET":"100","DG_LIVE_HANDICAP_DEFAULT_MAXBET":"1000000"}
    }
    // Only set once

    if (context.req) {
      app.context.req = context.req
    }
    if (context.res) {
      app.context.res = context.res
    }

    if (context.ssrContext) {
      app.context.ssrContext = context.ssrContext
    }
    app.context.redirect = (status, path, query) => {
      if (!status) {
        return
      }
      app.context._redirected = true
      // if only 1 or 2 arguments: redirect('/') or redirect('/', { foo: 'bar' })
      let pathType = typeof path
      if (typeof status !== 'number' && (pathType === 'undefined' || pathType === 'object')) {
        query = path || {}
        path = status
        pathType = typeof path
        status = 302
      }
      if (pathType === 'object') {
        path = app.router.resolve(path).route.fullPath
      }
      // "/absolute/route", "./relative/route" or "../relative/route"
      if (/(^[.]{1,2}\/)|(^\/(?!\/))/.test(path)) {
        app.context.next({
          path,
          query,
          status
        })
      } else {
        path = withQuery(path, query)
        if (process.server) {
          app.context.next({
            path,
            status
          })
        }
        if (process.client) {
          // https://developer.mozilla.org/en-US/docs/Web/API/Location/replace
          window.location.replace(path)

          // Throw a redirect error
          throw new Error('ERR_REDIRECT')
        }
      }
    }
    if (process.server) {
      app.context.beforeNuxtRender = fn => context.beforeRenderFns.push(fn)
    }
    if (process.client) {
      app.context.nuxtState = window.__NUXT__
    }
  }

  // Dynamic keys
  const [currentRouteData, fromRouteData] = await Promise.all([
    getRouteData(context.route),
    getRouteData(context.from)
  ])

  if (context.route) {
    app.context.route = currentRouteData
  }

  if (context.from) {
    app.context.from = fromRouteData
  }

  app.context.next = context.next
  app.context._redirected = false
  app.context._errored = false
  app.context.isHMR = false
  app.context.params = app.context.route.params || {}
  app.context.query = app.context.route.query || {}
}

export function middlewareSeries (promises, appContext) {
  if (!promises.length || appContext._redirected || appContext._errored) {
    return Promise.resolve()
  }
  return promisify(promises[0], appContext)
    .then(() => {
      return middlewareSeries(promises.slice(1), appContext)
    })
}

export function promisify (fn, context) {
  let promise
  if (fn.length === 2) {
    // fn(context, callback)
    promise = new Promise((resolve) => {
      fn(context, function (err, data) {
        if (err) {
          context.error(err)
        }
        data = data || {}
        resolve(data)
      })
    })
  } else {
    promise = fn(context)
  }

  if (promise && promise instanceof Promise && typeof promise.then === 'function') {
    return promise
  }
  return Promise.resolve(promise)
}

// Imported from vue-router
export function getLocation (base, mode) {
  if (mode === 'hash') {
    return window.location.hash.replace(/^#\//, '')
  }

  base = decodeURI(base).slice(0, -1) // consideration is base is normalized with trailing slash
  let path = decodeURI(window.location.pathname)

  if (base && path.startsWith(base)) {
    path = path.slice(base.length)
  }

  const fullPath = (path || '/') + window.location.search + window.location.hash

  return normalizeURL(fullPath)
}

// Imported from path-to-regexp

/**
 * Compile a string to a template function for the path.
 *
 * @param  {string}             str
 * @param  {Object=}            options
 * @return {!function(Object=, Object=)}
 */
export function compile (str, options) {
  return tokensToFunction(parse(str, options), options)
}

export function getQueryDiff (toQuery, fromQuery) {
  const diff = {}
  const queries = { ...toQuery, ...fromQuery }
  for (const k in queries) {
    if (String(toQuery[k]) !== String(fromQuery[k])) {
      diff[k] = true
    }
  }
  return diff
}

export function normalizeError (err) {
  let message
  if (!(err.message || typeof err === 'string')) {
    try {
      message = JSON.stringify(err, null, 2)
    } catch (e) {
      message = `[${err.constructor.name}]`
    }
  } else {
    message = err.message || err
  }
  return {
    ...err,
    message,
    statusCode: (err.statusCode || err.status || (err.response && err.response.status) || 500)
  }
}

/**
 * The main path matching regexp utility.
 *
 * @type {RegExp}
 */
const PATH_REGEXP = new RegExp([
  // Match escaped characters that would otherwise appear in future matches.
  // This allows the user to escape special characters that won't transform.
  '(\\\\.)',
  // Match Express-style parameters and un-named parameters with a prefix
  // and optional suffixes. Matches appear as:
  //
  // "/:test(\\d+)?" => ["/", "test", "\d+", undefined, "?", undefined]
  // "/route(\\d+)"  => [undefined, undefined, undefined, "\d+", undefined, undefined]
  // "/*"            => ["/", undefined, undefined, undefined, undefined, "*"]
  '([\\/.])?(?:(?:\\:(\\w+)(?:\\(((?:\\\\.|[^\\\\()])+)\\))?|\\(((?:\\\\.|[^\\\\()])+)\\))([+*?])?|(\\*))'
].join('|'), 'g')

/**
 * Parse a string for the raw tokens.
 *
 * @param  {string}  str
 * @param  {Object=} options
 * @return {!Array}
 */
function parse (str, options) {
  const tokens = []
  let key = 0
  let index = 0
  let path = ''
  const defaultDelimiter = (options && options.delimiter) || '/'
  let res

  while ((res = PATH_REGEXP.exec(str)) != null) {
    const m = res[0]
    const escaped = res[1]
    const offset = res.index
    path += str.slice(index, offset)
    index = offset + m.length

    // Ignore already escaped sequences.
    if (escaped) {
      path += escaped[1]
      continue
    }

    const next = str[index]
    const prefix = res[2]
    const name = res[3]
    const capture = res[4]
    const group = res[5]
    const modifier = res[6]
    const asterisk = res[7]

    // Push the current path onto the tokens.
    if (path) {
      tokens.push(path)
      path = ''
    }

    const partial = prefix != null && next != null && next !== prefix
    const repeat = modifier === '+' || modifier === '*'
    const optional = modifier === '?' || modifier === '*'
    const delimiter = res[2] || defaultDelimiter
    const pattern = capture || group

    tokens.push({
      name: name || key++,
      prefix: prefix || '',
      delimiter,
      optional,
      repeat,
      partial,
      asterisk: Boolean(asterisk),
      pattern: pattern ? escapeGroup(pattern) : (asterisk ? '.*' : '[^' + escapeString(delimiter) + ']+?')
    })
  }

  // Match any characters still remaining.
  if (index < str.length) {
    path += str.substr(index)
  }

  // If the path exists, push it onto the end.
  if (path) {
    tokens.push(path)
  }

  return tokens
}

/**
 * Prettier encoding of URI path segments.
 *
 * @param  {string}
 * @return {string}
 */
function encodeURIComponentPretty (str, slashAllowed) {
  const re = slashAllowed ? /[?#]/g : /[/?#]/g
  return encodeURI(str).replace(re, (c) => {
    return '%' + c.charCodeAt(0).toString(16).toUpperCase()
  })
}

/**
 * Encode the asterisk parameter. Similar to `pretty`, but allows slashes.
 *
 * @param  {string}
 * @return {string}
 */
function encodeAsterisk (str) {
  return encodeURIComponentPretty(str, true)
}

/**
 * Escape a regular expression string.
 *
 * @param  {string} str
 * @return {string}
 */
function escapeString (str) {
  return str.replace(/([.+*?=^!:${}()[\]|/\\])/g, '\\$1')
}

/**
 * Escape the capturing group by escaping special characters and meaning.
 *
 * @param  {string} group
 * @return {string}
 */
function escapeGroup (group) {
  return group.replace(/([=!:$/()])/g, '\\$1')
}

/**
 * Expose a method for transforming tokens into the path function.
 */
function tokensToFunction (tokens, options) {
  // Compile all the tokens into regexps.
  const matches = new Array(tokens.length)

  // Compile all the patterns before compilation.
  for (let i = 0; i < tokens.length; i++) {
    if (typeof tokens[i] === 'object') {
      matches[i] = new RegExp('^(?:' + tokens[i].pattern + ')$', flags(options))
    }
  }

  return function (obj, opts) {
    let path = ''
    const data = obj || {}
    const options = opts || {}
    const encode = options.pretty ? encodeURIComponentPretty : encodeURIComponent

    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i]

      if (typeof token === 'string') {
        path += token

        continue
      }

      const value = data[token.name || 'pathMatch']
      let segment

      if (value == null) {
        if (token.optional) {
          // Prepend partial segment prefixes.
          if (token.partial) {
            path += token.prefix
          }

          continue
        } else {
          throw new TypeError('Expected "' + token.name + '" to be defined')
        }
      }

      if (Array.isArray(value)) {
        if (!token.repeat) {
          throw new TypeError('Expected "' + token.name + '" to not repeat, but received `' + JSON.stringify(value) + '`')
        }

        if (value.length === 0) {
          if (token.optional) {
            continue
          } else {
            throw new TypeError('Expected "' + token.name + '" to not be empty')
          }
        }

        for (let j = 0; j < value.length; j++) {
          segment = encode(value[j])

          if (!matches[i].test(segment)) {
            throw new TypeError('Expected all "' + token.name + '" to match "' + token.pattern + '", but received `' + JSON.stringify(segment) + '`')
          }

          path += (j === 0 ? token.prefix : token.delimiter) + segment
        }

        continue
      }

      segment = token.asterisk ? encodeAsterisk(value) : encode(value)

      if (!matches[i].test(segment)) {
        throw new TypeError('Expected "' + token.name + '" to match "' + token.pattern + '", but received "' + segment + '"')
      }

      path += token.prefix + segment
    }

    return path
  }
}

/**
 * Get the flags for a regexp from the options.
 *
 * @param  {Object} options
 * @return {string}
 */
function flags (options) {
  return options && options.sensitive ? '' : 'i'
}

export function addLifecycleHook(vm, hook, fn) {
  if (!vm.$options[hook]) {
    vm.$options[hook] = []
  }
  if (!vm.$options[hook].includes(fn)) {
    vm.$options[hook].push(fn)
  }
}

export const urlJoin = joinURL

export const stripTrailingSlash = withoutTrailingSlash

export const isSamePath = _isSamePath

export function setScrollRestoration (newVal) {
  try {
    window.history.scrollRestoration = newVal;
  } catch(e) {}
}
